class FallbackDirectUpload {
  file: File;
  url: string;
  delegate: any;
  constructor(file: File, url: string, delegate?: any) {
    this.file = file;
    this.url = url;
    this.delegate = delegate;
  }
  create(callback: (error: Error | null, blob: any) => void) {
    callback(new Error("DirectUpload not available in this environment"), null);
  }
}

let DirectUpload: any = FallbackDirectUpload;

import("@rails/activestorage")
  .then((mod) => { DirectUpload = mod.DirectUpload; })
  .catch(() => { /* reste sur le fallback */ });

// social_network_id issu du load_book — envoyé en params de create_picture
let socialNetworkId: number | null = null;

export function setUploadSocialNetworkId(id: number | null | undefined): void {
  socialNetworkId = typeof id === 'number' ? id : null;
}

function getCsrfToken(): string {
  const token = document.querySelector('meta[name="csrf-token"]')?.getAttribute("content");
  if (!token) console.warn("[CSRF] meta csrf-token not found");
  return token ?? "";
}

type UploadResult =
  | { ok: true; file: File; signedId: string; width: number; height: number }
  | { ok: false; file: File; step: "direct_upload" | "associate"; error: any };

async function retry<T>(fn: () => Promise<T>, retries = 2, delayMs = 400): Promise<T> {
  try {
    return await fn();
  } catch (e) {
    if (retries <= 0) throw e;
    await new Promise((r) => setTimeout(r, delayMs));
    return retry(fn, retries - 1, delayMs * 2);
  }
}

function uploadToActiveStorage(file: File): Promise<{ signedId: string; blobId?: number }> {
  return new Promise((resolve, reject) => {
    const upload = new DirectUpload(file, "/rails/active_storage/direct_uploads", {
      directUploadWillCreateBlobWithXHR: (xhr: XMLHttpRequest) => xhr.setRequestHeader("Accept", "application/json"),
    });

    upload.create((error: Error | null, blob: any) => {
      if (error) return reject(error);
      resolve({ signedId: blob.signed_id, blobId: blob.id });
    });
  });
}

function isHeicOrHeif(file: File): boolean {
  const name = file.name.toLowerCase();
  return name.endsWith('.heic') || name.endsWith('.heif') || file.type === 'image/heic' || file.type === 'image/heif';
}

export function getImageSize(file: File): Promise<{ width: number; height: number }> {
  // Les navigateurs ne supportent pas HEIC/HEIF nativement,
  // on renvoie des dimensions par défaut — Rails convertira côté serveur.
  if (isHeicOrHeif(file)) {
    return Promise.resolve({ width: 0, height: 0 });
  }

  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      const width = img.naturalWidth;
      const height = img.naturalHeight;
      URL.revokeObjectURL(url);
      resolve({ width, height });
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      // Fallback pour tout format non lisible par le navigateur
      resolve({ width: 0, height: 0 });
    };
    img.src = url;
  });
}

async function associateToEvent(
  eventId: string,
  signedId: string, blobId: number | undefined,
  dims: { width: number; height: number }
): Promise<void> {
  const csrfToken = getCsrfToken();

  const res = await fetch(`/api/events/${eventId}/create_picture`, {
    method: "POST",
    credentials: "same-origin",
    headers: {
      "Content-Type": "application/json",
      "X-CSRF-Token": csrfToken,
      Accept: "application/json",
    },
    body: JSON.stringify({
      blob_signed_id: signedId,
      blob_id: blobId,
      width: dims.width,
      height: dims.height,
      ...(socialNetworkId !== null ? { social_network_id: socialNetworkId } : {}),
    }),
  });

  if (!res.ok) {
    let text = "";
    try {
      text = await res.text();
    } catch (_) {}
    throw new Error(`Associate failed: HTTP ${res.status} ${res.url} — ${text.slice(0, 200)}`);
  }
}

export async function uploadOne(file: File, eventId: string): Promise<UploadResult> {
  const t0 = performance.now();

  try {
    const sizePromise = getImageSize(file);

    const t1 = performance.now();
    const { signedId, blobId } = await uploadToActiveStorage(file);
    const t2 = performance.now();

    const dims = await sizePromise;

    await retry(() => associateToEvent(eventId, signedId, blobId, dims), 2);
    const t3 = performance.now();

    console.log("[UploadTiming]", file.name, {
      total_ms: Math.round(t3 - t0),
      direct_upload_ms: Math.round(t2 - t1),
      associate_ms: Math.round(t3 - t2),
      width: dims.width,
      height: dims.height,
    });

    return { ok: true, file, signedId, width: dims.width, height: dims.height };
  } catch (e: any) {
    const step: "direct_upload" | "associate" =
      String(e?.message || "").toLowerCase().includes("associate") ? "associate" : "direct_upload";
    return { ok: false, file, step, error: e };
  }
}

export async function uploadBatch(
  files: File[],
  eventId: string,
  opts?: {
    concurrency?: number;
    onProgress?: (done: number, total: number) => void;
    onFileDone?: (result: UploadResult) => void;
  }
): Promise<{ results: UploadResult[]; failed: UploadResult[] }> {
  const concurrency = Math.max(1, Math.min(opts?.concurrency ?? 4, 8));
  const total = files.length;

  let done = 0;
  const results: UploadResult[] = [];
  const queue = [...files];

  async function worker() {
    while (queue.length) {
      const file = queue.shift();
      if (!file) return;

      const result = await uploadOne(file, eventId);
      results.push(result);

      done++;
      opts?.onFileDone?.(result);
      opts?.onProgress?.(done, total);
    }
  }

  await Promise.all(Array.from({ length: concurrency }, () => worker()));

  const failed = results.filter((r): r is UploadResult & { ok: false } => !r.ok);
  return { results, failed };
}